import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, ChevronLeft, Mail } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function PreviewCards({ data }) {
  return (
    <div className="mt-6 grid sm:grid-cols-2 gap-4">
      <div className="paper-card overflow-hidden book-tilt" data-testid="preview-cover">
        <img src={`${BACKEND_URL}${data.cover_image}`} alt="cover" className="w-full h-72 object-cover" />
        <div className="p-3 text-center">
          <div className="text-xs font-bold tracking-widest text-[#E07A5F]">COVER</div>
          <div className="font-display font-bold">{data.title}</div>
        </div>
      </div>
      <div className="paper-card overflow-hidden" data-testid="preview-page-1">
        <img src={`${BACKEND_URL}${data.page_image}`} alt="page 1" className="w-full h-72 object-cover" />
        <div className="p-4 text-sm text-[#2B2D42]">
          <div className="text-xs font-bold tracking-widest text-[#E07A5F] mb-1">PAGE 1</div>
          {data.first_page_text}
        </div>
      </div>
    </div>
  );
}

function CheckoutPanel({ email, setEmail, onCheckout, loading }) {
  return (
    <div className="paper-card p-6 sticky top-24">
      <div className="text-sm text-[#5C6370]">Your personalized book</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-5xl font-display font-black text-[#E07A5F]">$8</span>
        <span className="text-sm text-[#5C6370]">one-time, USD</span>
      </div>
      <ul className="mt-4 text-sm text-[#2B2D42] space-y-1">
        <li>✦ 8 illustrated pages, same character throughout</li>
        <li>✦ Instant PDF download + email</li>
        <li>✦ Saved forever in your library</li>
      </ul>
      <div className="mt-6">
        <Label>Email for download</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" size={16} />
          <Input data-testid="checkout-email" className="pl-9" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="parent@example.com" />
        </div>
      </div>
      <Button data-testid="checkout-btn" onClick={onCheckout} disabled={loading || !email}
        className="mt-4 w-full bg-[#2B2D42] hover:bg-[#3D405B] text-white rounded-full py-6 text-base">
        {loading ? (<><Loader2 className="animate-spin mr-2" size={16} /> Redirecting…</>)
                : (<><Sparkles size={16} className="mr-2" /> Make it real — $8</>)}
      </Button>
      <p className="mt-3 text-xs text-[#6C757D] text-center">
        Secure checkout via Stripe. Test card: 4242 4242 4242 4242
      </p>
    </div>
  );
}

export default function Preview() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("sf_preview");
    if (!raw) { nav("/create"); return; }
    try { setData(JSON.parse(raw)); }
    catch (e) { console.error("Failed to parse preview data:", e); nav("/create"); }
  }, [nav]);

  const checkout = async () => {
    if (!email) { toast.error("Enter an email so we can send the book."); return; }
    setLoading(true);
    try {
      const { data: res } = await http.post("/checkout", {
        email, child: data.child, story: data.story,
        preview_id: data.preview_id, origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error("Couldn't start checkout — try again.");
      setLoading(false);
    }
  };

  if (!data) return null;

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="max-w-5xl mx-auto px-5 py-10">
        <button onClick={() => nav("/create")} data-testid="preview-back"
          className="text-sm text-[#4A4E69] flex items-center gap-1 hover:text-[#E07A5F]">
          <ChevronLeft size={16} /> Back to editor
        </button>
        <div className="mt-4 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <span className="pill-tag bg-[#F2CC8F]/60 text-[#5C3F1E]">Free preview</span>
            <h1 className="font-display text-4xl font-bold mt-3 text-[#2B2D42]">{data.title}</h1>
            <p className="mt-2 text-[#4A4E69]">A first look at the book we've dreamed up for {data.child.name}.</p>
            <PreviewCards data={data} />
            <p className="mt-4 text-xs text-[#6C757D]">
              7 more pages (with images that keep {data.child.name}'s character consistent) unlock after checkout.
            </p>
          </div>
          <div className="lg:col-span-2">
            <CheckoutPanel email={email} setEmail={setEmail} onCheckout={checkout} loading={loading} />
          </div>
        </div>
      </main>
    </div>
  );
}
